import type { CSSProperties, ReactNode } from 'react';
import { Toaster as SonnerToaster, toast } from 'sonner';
import { ApiErrorCode, isApiError } from '@/shared/api';
import { getActionErrorMessage, getErrorMessage } from './errorMessage';

declare module 'react' {
  interface CSSProperties {
    /** Ширина области тостов sonner. */
    '--width'?: string;
  }
}

/**
 * Тост (README «Оверлеи»): снизу по центру (bottom 20), padding 10 16, radius 8, фон wine-ink,
 * тень тоста, точка gold-leaf, текст 13/20 rail-text, max-width 520 (по содержимому),
 * скрывается через 3,2 с.
 */
const TOAST_DURATION_MS = 3200;
const TOAST_MAX_WIDTH = '520px';

const TOASTER_STYLE: CSSProperties = { '--width': TOAST_MAX_WIDTH };

export function Toaster() {
  return (
    <SonnerToaster
      position="bottom-center"
      offset={20}
      duration={TOAST_DURATION_MS}
      visibleToasts={3}
      style={TOASTER_STYLE}
      toastOptions={{ unstyled: true, className: 'w-full' }}
    />
  );
}

/** Переход из тоста к созданной записи («Открыть заказ»): тост закрывается, вызывается `onClick`. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

function ToastBody({ children, action }: { children: ReactNode; action?: ToastAction }) {
  return (
    <div className="mx-auto flex w-fit max-w-520 items-center gap-10 rounded-8 bg-wine-ink px-16 py-10 font-ui shadow-toast">
      <span aria-hidden="true" className="size-6 shrink-0 rounded-pill bg-gold-leaf" />
      <span className="text-[13px] leading-[20px] text-rail-text">{children}</span>
      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-[13px] leading-[20px] font-semibold whitespace-nowrap text-gold-leaf hover:underline"
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Сообщить результат действия («Логотип заменён · logo.svg»); `action` — переход к созданной
 * записи («Открыть заказ»), тост при этом закрывается.
 */
export function notify(message: string, action?: ToastAction): void {
  toast.custom(
    (id) => (
      <ToastBody
        action={
          action
            ? {
                label: action.label,
                onClick: () => {
                  toast.dismiss(id);
                  action.onClick();
                },
              }
            : undefined
        }
      >
        {message}
      </ToastBody>
    ),
    { duration: TOAST_DURATION_MS },
  );
}

/** Тост об ошибке API с локализованным сообщением. */
export function notifyError(error: unknown): void {
  notify(getErrorMessage(error));
}

/**
 * Тост об ошибке действия вне формы (`meta.errorToast: false` у мутации): тексты полей `422`/`409`
 * или общее сообщение. 401 (переход на вход) и отмену запроса не показывает.
 */
export function notifyActionError(error: unknown): void {
  if (
    isApiError(error) &&
    (error.code === ApiErrorCode.Unauthorized || error.code === ApiErrorCode.Aborted)
  ) {
    return;
  }
  notify(getActionErrorMessage(error));
}
